import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Phone } from "lucide-react";
import { CITY_INDEX } from "@/data/cities";
import { SERVICES } from "@/data/services";
import { SectionHeader, Reveal } from "@/components/SectionHeader";
import SEO, { breadcrumbJsonLd } from "@/components/SEO";

export default function CityPage() {
  const { city } = useParams();
  const c = CITY_INDEX[city];

  if (!c) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-display text-3xl font-bold">City not found</h1>
        <Link to="/locations" className="btn-primary mt-8 inline-flex">All Texas locations</Link>
      </div>
    );
  }

  return (
    <div data-testid={`city-page-${c.slug}`}>
      <SEO
        title={`Environmental Assessments in ${c.name}, TX — Hurtler`}
        description={`Independent mold, IAQ, moisture, and water-damage assessments in ${c.name}, ${c.county}. Hurtler Environmental serves the ${c.region}.`}
        path={`/locations/${c.slug}`}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Texas Locations", path: "/locations" },
          { name: c.name, path: `/locations/${c.slug}` },
        ])}
      />
      <section className="bg-[#0F172A] text-white py-24">
        <div className="container-x">
          <Link to="/locations" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-8">
            <ArrowLeft className="w-4 h-4" /> All Texas locations
          </Link>
          <div className="overline text-[#F59E0B] mb-4">
            {c.county} • {c.region}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
            Independent Environmental Assessments in <span className="text-[#F59E0B]">{c.name}, Texas</span>
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">{c.blurb}</p>
          {c.isHQ && (
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/30 text-sm">
              <MapPin className="w-4 h-4 text-[#F59E0B]" />
              204 E Cano Street, Edinburg TX 78539 — Hurtler Headquarters
            </div>
          )}
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/schedule" data-testid={`city-schedule-${c.slug}`} className="btn-primary">
              Schedule in {c.name} <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a href="tel:9562172604" className="btn-outline-dark">
              <Phone className="w-4 h-4 mr-2" /> 956.217.2604
            </a>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x">
          <SectionHeader
            overline={`Services in ${c.name}`}
            title={`Our full consulting practice — locally available in ${c.name}.`}
            subtitle="Every Hurtler service is performed with the same scientific rigor and the same structural independence from remediation — no matter the city."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={i * 0.03}>
                  <Link
                    to={`/locations/${c.slug}/${s.slug}`}
                    data-testid={`city-service-${c.slug}-${s.slug}`}
                    className="card-soft block p-7 h-full"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#7C3AED]/10 grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-[#7C3AED]" />
                    </div>
                    <h3 className="font-display font-bold text-[#0F172A] mb-2">{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{s.summary}</p>
                    <div className="mt-5 text-sm font-semibold text-[#7C3AED] inline-flex items-center gap-2">
                      {s.title} in {c.name} <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#F8FAFC]">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline mb-4">Why {c.name} chooses Hurtler</div>
            <h2 className="font-display text-3xl font-bold text-[#0F172A] leading-tight">
              The only independent voice in {c.region}.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              Most firms in {c.county} and surrounding areas also sell remediation services.
              Hurtler does not. That single structural difference removes every conflict of
              interest from your assessment — and gives your report the kind of credibility
              that withstands carriers, contractors, and courts.
            </p>
            <div className="mt-8">
              <Link to={`/schedule`} className="btn-primary" data-testid={`city-cta-${c.slug}`}>
                Schedule in {c.name} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
          <div className="card-soft p-8">
            <div className="overline mb-4">Local service area</div>
            <ul className="space-y-3 text-slate-700">
              <li>{c.name} city limits & metro</li>
              <li>{c.county} unincorporated areas</li>
              <li>{c.region}</li>
              <li>Surrounding South Texas regions</li>
            </ul>
            <div className="mt-6 text-xs text-slate-500 leading-relaxed">
              Travel beyond standard service radius may be quoted separately for commercial
              and litigation engagements.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
